/** Plant overhead obligations: folders, loan balances, and the monthly consumption day. */

export const OVERHEAD_BUCKET_SENSITIVE_NOTE =
  "The name and notes are visible to everyone who can open Overhead. Do not put bank account numbers here. A nickname is enough to tell one loan or lease from another.";

export type ConsumptionSetting = {
  day: number | null;
  usesLastDay: boolean;
};

export type PoolBucket = {
  id: string;
  parent_bucket_id: string | null;
  is_active: boolean;
  is_aggregate: boolean;
  retired_on: string | null;
};

export type BalanceBucket = PoolBucket & {
  tracks_balance: boolean;
  balance_start_usd: number | null;
  balance_as_of: string | null;
  principal_monthly_usd: number | null;
};

export type StoredPrincipalLine = {
  bucket_id: string;
  period_start: string;
  principal_consumed_usd: number | null;
};

export type PlannedConsumptionLine = {
  bucket_id: string;
  payment_usd: number | null;
  principal_consumed_usd: number | null;
  balance_after_usd: number | null;
};

export function roundUsd(value: number): number {
  return Number(value.toFixed(2));
}

export function utcToday(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function periodStartOf(isoDate: string): string {
  return `${isoDate.slice(0, 7)}-01`;
}

export function addMonths(periodStart: string, delta: number): string {
  const year = Number(periodStart.slice(0, 4));
  const month = Number(periodStart.slice(5, 7));
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
  const nextMonth = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  return `${shifted.getUTCFullYear()}-${nextMonth}-01`;
}

export function lastDayIso(periodStart: string): string {
  const year = Number(periodStart.slice(0, 4));
  const month = Number(periodStart.slice(5, 7));
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}

export function isConsumptionConfigured(setting: ConsumptionSetting): boolean {
  return setting.usesLastDay || (setting.day != null && setting.day >= 1 && setting.day <= 28);
}

export function consumptionDateForPeriod(periodStart: string, setting: ConsumptionSetting): string | null {
  if (!isConsumptionConfigured(setting)) return null;
  if (setting.usesLastDay) return lastDayIso(periodStart);
  const day = String(setting.day).padStart(2, "0");
  return `${periodStart.slice(0, 7)}-${day}`;
}

export function exclusionDateForPeriod(periodStart: string, setting: ConsumptionSetting): string {
  return consumptionDateForPeriod(periodStart, setting) ?? lastDayIso(periodStart);
}

export function upcomingConsumptionDate(today: string, setting: ConsumptionSetting): string | null {
  const thisPeriod = periodStartOf(today);
  const thisDate = consumptionDateForPeriod(thisPeriod, setting);
  if (!thisDate) return null;
  if (today <= thisDate) return thisDate;
  return consumptionDateForPeriod(addMonths(thisPeriod, 1), setting);
}

export function periodsDueForConsumption(
  today: string,
  setting: ConsumptionSetting,
  consumedPeriodStarts: Iterable<string>,
): string[] {
  if (!isConsumptionConfigured(setting)) return [];
  const consumed = new Set(consumedPeriodStarts);
  const current = periodStartOf(today);
  const due: string[] = [];
  for (const period of [addMonths(current, -1), current]) {
    const date = consumptionDateForPeriod(period, setting);
    if (date && date <= today && !consumed.has(period)) due.push(period);
  }
  return due;
}

export function isRetiredForPeriod(bucket: PoolBucket, periodStart: string, setting: ConsumptionSetting): boolean {
  if (!bucket.retired_on) return false;
  return bucket.retired_on <= exclusionDateForPeriod(periodStart, setting);
}

export function poolBucketIds(
  buckets: PoolBucket[],
  hasValue: (id: string) => boolean,
  periodStart: string,
  setting: ConsumptionSetting,
  includeInactive = false,
): Set<string> {
  const eligible = (bucket: PoolBucket) =>
    (includeInactive || bucket.is_active) && !isRetiredForPeriod(bucket, periodStart, setting);

  const childHasValue = (parentId: string) =>
    buckets.some(
      (bucket) => bucket.parent_bucket_id === parentId && eligible(bucket) && hasValue(bucket.id),
    );

  const ids = new Set<string>();
  for (const bucket of buckets) {
    if (!eligible(bucket)) continue;
    if (bucket.is_aggregate && childHasValue(bucket.id)) continue;
    ids.add(bucket.id);
  }
  return ids;
}

export function sumPoolUsd(
  buckets: PoolBucket[],
  amountOf: (id: string) => number | null,
  periodStart: string,
  setting: ConsumptionSetting,
  includeInactive = false,
): number {
  const ids = poolBucketIds(buckets, (id) => amountOf(id) != null, periodStart, setting, includeInactive);
  let total = 0;
  for (const id of ids) {
    const amount = amountOf(id);
    if (amount != null) total += amount;
  }
  return roundUsd(total);
}

export function paymentUsd(assumed: number | null, actual: number | null): number | null {
  if (typeof actual === "number" && Number.isFinite(actual)) return actual;
  if (typeof assumed === "number" && Number.isFinite(assumed)) return assumed;
  return null;
}

export function remainingBalanceUsd(
  bucket: BalanceBucket,
  lines: StoredPrincipalLine[],
  beforePeriod?: string,
): number | null {
  if (!bucket.tracks_balance || bucket.balance_start_usd == null || !bucket.balance_as_of) return null;
  let used = 0;
  for (const line of lines) {
    if (line.bucket_id !== bucket.id) continue;
    if (line.period_start < bucket.balance_as_of) continue;
    if (beforePeriod && line.period_start >= beforePeriod) continue;
    if (typeof line.principal_consumed_usd === "number") used += line.principal_consumed_usd;
  }
  return roundUsd(Math.max(0, bucket.balance_start_usd - used));
}

export function planConsumption(input: {
  periodStart: string;
  setting: ConsumptionSetting;
  buckets: BalanceBucket[];
  payments: Map<string, number | null>;
  priorLines: StoredPrincipalLine[];
}): { lines: PlannedConsumptionLine[]; overhead_total_usd: number } {
  const ids = poolBucketIds(
    input.buckets,
    (id) => input.payments.get(id) != null,
    input.periodStart,
    input.setting,
  );
  const lines: PlannedConsumptionLine[] = [];
  let total = 0;
  for (const bucket of input.buckets) {
    if (!ids.has(bucket.id)) continue;
    const payment = input.payments.get(bucket.id) ?? null;
    let principal: number | null = null;
    let balanceAfter: number | null = null;
    const started = !bucket.balance_as_of || bucket.balance_as_of <= input.periodStart;
    if (bucket.tracks_balance && !bucket.is_aggregate && started) {
      const remaining = remainingBalanceUsd(bucket, input.priorLines, input.periodStart);
      if (remaining != null) {
        const standing = bucket.principal_monthly_usd ?? 0;
        principal = roundUsd(Math.min(Math.max(0, standing), remaining));
        balanceAfter = roundUsd(remaining - principal);
      }
    }
    if (payment == null && principal == null) continue;
    if (payment != null) total += payment;
    lines.push({
      bucket_id: bucket.id,
      payment_usd: payment,
      principal_consumed_usd: principal,
      balance_after_usd: balanceAfter,
    });
  }
  return { lines, overhead_total_usd: roundUsd(total) };
}

export type ObligationFields = {
  is_aggregate: boolean;
  tracks_balance: boolean;
  balance_start_usd: number | null;
  balance_as_of: string | null;
  principal_monthly_usd: number | null;
};

export function validateObligationFields(
  fields: ObligationFields & { parent_bucket_id: string | null },
): string | null {
  if (fields.is_aggregate && fields.parent_bucket_id) {
    return "An aggregate bucket stays at the top and totals the buckets under it.";
  }
  if (fields.is_aggregate && fields.tracks_balance) {
    return "An aggregate bucket totals its children and does not track a balance.";
  }
  if (!fields.tracks_balance) return null;
  if (fields.balance_start_usd == null || fields.balance_start_usd < 0) {
    return "Enter the balance as of the start month.";
  }
  if (!fields.balance_as_of) return "Enter the month the balance is as of.";
  if (fields.principal_monthly_usd == null || fields.principal_monthly_usd < 0) {
    return "Enter the principal paid down each month.";
  }
  return null;
}

export function normalizeBalanceAsOf(value: string): string | null {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}$/.test(trimmed)) return `${trimmed}-01`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return `${trimmed.slice(0, 7)}-01`;
  return null;
}
